import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../App';
import MatchDetail from './MatchDetail';
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const BOOKMAKER_URLS = {
  betway_bw: 'https://www.betway.co.bw',
  pstbets_bw: 'https://www.pstbet.co.bw',
  sunbet_bw: 'https://www.sunbet.co.bw',
  bet267_bw: 'https://www.bet267.co.bw',
  bbets_bw: 'https://www.bbets.co.bw',
  betxplosion_bw: 'https://www.betxplosion.co.bw',
};

const SPORT_EMOJI = {
  soccer: '⚽', football: '⚽', basketball: '🏀', tennis: '🎾',
  'american-football': '🏈', cricket: '🏏', rugby: '🏉', 'rugby-union': '🏉',
  handball: '🤾', 'ice-hockey': '🏒', baseball: '⚾', futsal: '⚽',
  'table-tennis': '🏓', volleyball: '🏐', badminton: '🏸', 'water-polo': '🤽',
  boxing: '🥊', 'rugby-league': '🏉',
};

const SPORT_LABELS = {
  soccer: 'Football', football: 'Football', 'ice-hockey': 'Ice Hockey', 'rugby-union': 'Rugby Union',
  'rugby-league': 'Rugby League', 'american-football': 'American Football', 'table-tennis': 'Table Tennis',
  'water-polo': 'Water Polo',
};

function sportLabel(s = '') {
  return SPORT_LABELS[s] || s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Used until the real list of sports has loaded from the server
const FALLBACK_SPORTS = ['soccer', 'basketball', 'tennis', 'ice-hockey', 'handball', 'rugby-union', 'cricket', 'american-football', 'baseball'];

function sportEmoji(s = '') {
  return SPORT_EMOJI[s.toLowerCase()] || '🎯';
}

function formatKickoff(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('en-BW', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' + d.toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
}

function MatchCard({ item, onSelect, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const isArb = item.status === 'ARB';
  const isNear = item.status === 'NEAR_ARB';
  const kickoff = item.startTime || item.commenceTime;

  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      background: isArb ? 'rgba(18,201,142,0.03)' : 'transparent',
    }}>
      {/* Main row */}
      <div
        style={{ padding: isMobile ? '12px 14px' : '14px 20px', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14 }}>{sportEmoji(item.sport)}</span>
              <span style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14 }}>
                {item.homeTeam} vs {item.awayTeam}
              </span>
              {isArb && <span className="pill pill-green" style={{ fontSize: 9 }}>⚡ ARB</span>}
              {isNear && !isArb && <span className="pill pill-amber" style={{ fontSize: 9 }}>Near ARB</span>}
              {item.isLive && <span className="pill" style={{ fontSize: 9, background: 'rgba(240,71,71,0.15)', color: 'var(--danger)' }}>● LIVE</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {item.league || item.sport} · {formatKickoff(kickoff)}
            </div>
          </div>

          {/* Best 1X2 odds */}
          <div style={{ display: 'flex', gap: isMobile ? 8 : 16, alignItems: 'center', flexShrink: 0 }}>
            {item.bestHome && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>1</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>{item.bestHome.price}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestHome.bookmaker]?.split(' ')[0]}</div>
              </div>
            )}
            {item.bestDraw && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>X</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>{item.bestDraw.price}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestDraw.bookmaker]?.split(' ')[0]}</div>
              </div>
            )}
            {item.bestAway && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 2 }}>2</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>{item.bestAway.price}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestAway.bookmaker]?.split(' ')[0]}</div>
              </div>
            )}
            <div style={{ color: 'var(--text3)', fontSize: 12, marginLeft: 4 }}>{expanded ? '▲' : '▼'}</div>
          </div>
        </div>

        {/* Book % */}
        {item.bookPercent && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12 }}>
            <span>Book: <span style={{ color: isArb ? 'var(--success)' : 'var(--text2)', fontWeight: 600 }}>{item.bookPercent?.toFixed(2)}%</span></span>
            {item.arbMargin != null && (
              <span>Margin: <span style={{ color: item.arbMargin > 0 ? 'var(--success)' : 'var(--text3)', fontWeight: 600 }}>
                {item.arbMargin > 0 ? '+' : ''}{item.arbMargin?.toFixed(2)}%
              </span></span>
            )}
          </div>
        )}
      </div>

      {/* Expanded markets */}
            {expanded && (
        <div style={{ padding: isMobile ? '0 14px 14px' : '0 20px 16px', borderTop: '1px solid var(--border)' }}>
          
          {/* Quick links */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, paddingTop: 12 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Open:</span>
            {[...new Set([
              item.bestHome?.bookmaker,
              item.bestDraw?.bookmaker,
              item.bestAway?.bookmaker,
            ].filter(Boolean))].map(bm => (
              <a key={bm} href={BOOKMAKER_URLS[bm]} target="_blank" rel="noopener noreferrer"
                className="btn btn-outline" style={{ fontSize: 10, padding: '3px 10px' }}
                onClick={e => e.stopPropagation()}>
                {BOOKMAKER_LABELS[bm]} ↗
              </a>
            ))}
            <button
              className="btn btn-primary"
              style={{ fontSize: 10, padding: '3px 10px', marginLeft: 'auto' }}
              onClick={e => { e.stopPropagation(); onSelect(item); }}
            >
              Full Detail →
            </button>
          </div>

          <ExpandedMarkets item={item} isMobile={isMobile} />
        </div>
      )}
    </div>
  );
}

function ExpandedMarkets({ item, isMobile }) {
  const [marketTab, setMarketTab] = useState('result');

  const tabs = [
    { key: 'result', label: 'Match Result' },
    { key: 'goals', label: 'Goals' },
    { key: 'other', label: 'Other' },
  ].filter(t => {
    if (t.key === 'result') return item.bestHome || item.bestDraw || item.bestAway;
    if (t.key === 'goals') return item.ouMarkets?.length > 0 || item.bestBttsYes || item.bestBttsNo;
    if (t.key === 'other') return item.best1X || item.bestX2 || item.best12;
    return false;
  });

  return (
    <div>
      {/* Market tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={e => { e.stopPropagation(); setMarketTab(t.key); }} style={{
            padding: '8px 14px', fontSize: 11, fontFamily: 'var(--font-display)',
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5,
            color: marketTab === t.key ? 'var(--accent)' : 'var(--text3)',
            background: 'transparent', border: 'none',
            borderBottom: marketTab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
            cursor: 'pointer',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Match Result */}
      {marketTab === 'result' && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11, width: '40%' }}></th>
              <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Home</th>
              {item.bestDraw && <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Draw</th>}
              <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Away</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Best Price</td>
              <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestHome?.price ?? '—'}</td>
              {item.bestDraw && <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestDraw?.price ?? '—'}</td>}
              <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestAway?.price ?? '—'}</td>
            </tr>
            <tr style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Bookmaker</td>
              <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestHome?.bookmaker] || '—'}</td>
              {item.bestDraw && <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestDraw?.bookmaker] || '—'}</td>}
              <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestAway?.bookmaker] || '—'}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Goals */}
      {marketTab === 'goals' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* O/U */}
          {item.ouMarkets?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Over / Under</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Line</th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Over</th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Book</th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Under</th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Book</th>
                  </tr>
                </thead>
                <tbody>
                  {item.ouMarkets.sort((a, b) => a.handicap - b.handicap).map(ou => (
                    <tr key={ou.handicap} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px', fontWeight: 600 }}>{ou.handicap}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)' }}>{ou.over?.price ?? '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: 10, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[ou.over?.bookmaker]?.split(' ')[0] || '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)' }}>{ou.under?.price ?? '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: 10, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[ou.under?.bookmaker]?.split(' ')[0] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* BTTS */}
          {(item.bestBttsYes || item.bestBttsNo) && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Both Teams to Score</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}></th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Yes</th>
                    <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>No</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Best Price</td>
                    <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestBttsYes?.price ?? '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestBttsNo?.price ?? '—'}</td>
                  </tr>
                  <tr style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Bookmaker</td>
                    <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestBttsYes?.bookmaker] || '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestBttsNo?.bookmaker] || '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Other */}
      {marketTab === 'other' && (
        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Double Chance</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}></th>
                <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>1X</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>X2</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>12</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Best Price</td>
                <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.best1X?.price ?? '—'}</td>
                <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.bestX2?.price ?? '—'}</td>
                <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--success)' }}>{item.best12?.price ?? '—'}</td>
              </tr>
              <tr style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '8px', fontSize: 11, color: 'var(--text3)' }}>Bookmaker</td>
                <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.best1X?.bookmaker] || '—'}</td>
                <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestX2?.bookmaker] || '—'}</td>
                <td style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.best12?.bookmaker] || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function BestOdds() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [sport, setSport] = useState('soccer');
  const [league, setLeague] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tab, setTab] = useState('prematch');
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [counts, setCounts] = useState({ prematch: 0, live: 0 });
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  // Load sports and summary on mount
  useEffect(() => {
    Promise.all([
      apiFetch('/best-odds/sports'),
      apiFetch('/best-odds/summary'),
    ]).then(([sportsData, sumData]) => {
      setSports(sportsData.sports || []);
      setSummary(sumData.summary);
    }).catch(() => {});
  }, []);

  // Load leagues when sport changes
  useEffect(() => {
    apiFetch(`/best-odds/leagues?sport=${sport}`)
      .then(data => setLeagues(data.leagues || []))
      .catch(() => {});
    setLeague('ALL');
  }, [sport]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200, isLive: tab === 'live' });
      if (sport !== 'ALL') params.set('sport', sport);
      if (league !== 'ALL') params.set('league', league);
      if (status !== 'ALL') params.set('status', status);
      if (search.trim()) params.set('search', search.trim());

      const [currentData, otherData, sumData] = await Promise.all([
        apiFetch(`/best-odds?${params}`),
        apiFetch(`/best-odds?limit=1&isLive=${tab !== 'live'}`),
        apiFetch('/best-odds/summary'),
      ]);

      setItems(currentData.items || []);
      setCounts({
        prematch: tab === 'live' ? otherData.total : currentData.total,
        live: tab === 'live' ? currentData.total : otherData.total,
      });
      setSummary(sumData.summary);
      setLastRefresh(new Date());
    } catch (err) { console.error(err); }
    finally { setLoading(false); setInitialLoad(false); }
  }, [tab, sport, league, status, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const interval = tab === 'live' ? 30000 : 120000;
    const t = setInterval(() => { if (!document.hidden) load(); }, interval);
    const handleVisibility = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [load, tab]);

  if (selected) return <MatchDetail item={selected} onBack={() => setSelected(null)} />;

  const bestArb = items.find(i => i.status === 'ARB');

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      <div style={{ fontSize: 12, color: 'var(--text3)', padding: '6px 12px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', marginBottom: 8 }}>
  {tab === 'live'
    ? '⚡ Live odds update every 30 seconds — verify before betting'
    : '🕐 Odds updated every 5 minutes — verify before betting'}
</div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10 }}>
        {[
          { label: 'Prematch', value: counts.prematch, color: 'var(--accent)' },
          { label: 'Live', value: counts.live, color: 'var(--success)' },
          { label: 'Arb Opportunities', value: summary?.arbs ?? '—', color: 'var(--accent2)' },
          { label: 'Near ARB', value: summary?.nearArbs ?? '—', color: 'var(--warning)' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ '--accent-color': s.color, textAlign: 'center' }}>
            <div className="stat-label" style={{ textAlign: 'center', fontSize: 10 }}>{s.label}</div>
            <div className="stat-value" style={{ textAlign: 'center', fontSize: isMobile ? 18 : 24 }}>{loading && initialLoad ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs + filters */}
      <div className="card" style={{ padding: '0 18px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          {[
            { key: 'prematch', label: '📅 Pre-match' },
            { key: 'live', label: '🔴 Live' },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); setItems([]); setInitialLoad(true); }} style={{
              padding: '12px 18px', fontFamily: 'var(--font-display)', fontWeight: 600,
              fontSize: 12, textTransform: 'uppercase', letterSpacing: .5,
              color: tab === t.key ? 'var(--accent)' : 'var(--text3)',
              background: 'transparent', border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
              cursor: 'pointer',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '12px 0', alignItems: 'center' }}>
          {/* Sport */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>Sport</label>
            <select value={sport} onChange={e => setSport(e.target.value)} style={{ fontSize: 13 }}>
              <option value="ALL">All</option>
              {(sports.length ? sports : FALLBACK_SPORTS).map(sp => (
                <option key={sp} value={sp}>{sportEmoji(sp)} {sportLabel(sp)}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ fontSize: 13 }}>
              <option value="ALL">All</option>
              <option value="ARB">⚡ ARB only</option>
              <option value="NEAR_ARB">Near ARB</option>
            </select>
          </div>

          {/* League */}
          {leagues.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>League</label>
              <select value={league} onChange={e => setLeague(e.target.value)} style={{ fontSize: 13, maxWidth: 160 }}>
                <option value="ALL">All</option>
                {leagues.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}

          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
            <input
              type="text" placeholder="Search team or league..."
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              style={{ width: '100%', fontSize: 13, padding: '6px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)' }}
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearch(''); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14 }}>✕</button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={load} disabled={loading}>
              {loading ? '...' : '↺'}
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div style={{ fontSize: 12, color: 'var(--text3)', paddingLeft: 4 }}>
          {items.length} matches
          {search ? ` for "${search}"` : ''}
          {league !== 'ALL' ? ` in ${league}` : ''}
        </div>
      )}

      {/* Match list */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading && initialLoad ? (
          <div style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 72, marginBottom: 10 }} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <p>No matches found</p>
          </div>
        ) : items.map(item => (
          <MatchCard
            key={item._id}
            item={item}
            onSelect={setSelected}
            isMobile={isMobile}
          />
        ))}
      </div>
    </div>
  );
}